import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { MessageCircle, ArrowUp, Clock, Settings, X, RefreshCw, ExternalLink } from 'lucide-react'
import { redditAPI, widgetAPI } from '../../services/api'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { formatDistanceToNow } from 'date-fns'

interface RedditWidgetProps {
  widget: any
  isExpanded: boolean
}

export default function RedditWidget({ widget, isExpanded }: RedditWidgetProps) {
  const [showSettings, setShowSettings] = useState(false)
  const [selectedSubreddit, setSelectedSubreddit] = useState(
    widget.config?.subreddit || 'popular'
  )
  const queryClient = useQueryClient()

  // Fetch popular subreddits
  const { data: subredditsData } = useQuery({
    queryKey: ['reddit-subreddits'],
    queryFn: async () => {
      const { data } = await redditAPI.getPopularSubreddits()
      return data
    },
  })

  // Fetch Reddit posts
  const { data: postsData, isLoading, refetch } = useQuery({
    queryKey: ['reddit-posts', selectedSubreddit],
    queryFn: async () => {
      const { data } = await redditAPI.getPosts(selectedSubreddit, isExpanded ? 50 : 10)
      return data
    },
    refetchInterval: 600000, // Refresh every 10 minutes
  })

  const handleSubredditChange = async (subreddit: string) => {
    setSelectedSubreddit(subreddit)
    
    try {
      const newConfig = {
        ...widget.config,
        subreddit
      }
      await widgetAPI.update(widget.id, {
        config_data: JSON.stringify(newConfig)
      })
      queryClient.invalidateQueries({ queryKey: ['reddit-posts'] })
      queryClient.invalidateQueries({ queryKey: ['widgets'] })
      toast.success('Subreddit updated!')
      setShowSettings(false)
    } catch (error) {
      toast.error('Failed to update subreddit')
    }
  }

  const formatScore = (score: number) => {
    if (score >= 1000) {
      return `${(score / 1000).toFixed(1)}k`
    }
    return score.toString()
  }

  if (showSettings) {
    return (
      <div className="h-full flex flex-col p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Select Subreddit
          </h3>
          <button
            onClick={() => setShowSettings(false)}
            className="p-1 rounded hover:opacity-70"
            style={{ color: 'var(--color-text-primary)' }}
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 gap-2">
            {subredditsData?.subreddits?.map((sub: any) => (
              <button
                key={sub.name}
                onClick={() => handleSubredditChange(sub.name)}
                className={`text-left px-4 py-3 rounded-lg border-2 transition ${
                  selectedSubreddit === sub.name
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-transparent hover:bg-gray-100'
                }`}
                style={{
                  borderColor: selectedSubreddit === sub.name ? 'var(--color-primary)' : undefined,
                  backgroundColor: selectedSubreddit === sub.name ? 'var(--color-primary-100)' : undefined,
                }}
              >
                <div className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                  r/{sub.name}
                </div>
                <div className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                  {sub.display}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <RefreshCw className="animate-spin" size={32} style={{ color: 'var(--color-text-secondary)' }} />
      </div>
    )
  }

  const posts = postsData?.posts || []

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: 'var(--color-border)' }}>
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold" style={{ color: 'var(--color-primary)' }}>
            r/{selectedSubreddit}
          </span>
          <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            {posts.length} posts
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => refetch()}
            className="p-1.5 rounded transition hover:opacity-70"
            style={{ color: 'var(--color-text-primary)' }}
            title="Refresh"
          >
            <RefreshCw size={14} />
          </button>
          <button
            onClick={() => setShowSettings(true)}
            className="p-1.5 rounded transition hover:opacity-70"
            style={{ color: 'var(--color-text-primary)' }}
            title="Settings"
          >
            <Settings size={14} />
          </button>
        </div>
      </div>

      {/* Posts List */}
      <div className="flex-1 overflow-y-auto">
        {posts.length === 0 ? (
          <div className="flex items-center justify-center h-full p-4 text-center">
            <div>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                No posts available
              </p>
            </div>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
            {posts.map((post: any) => (
              <a
                key={post.id}
                href={post.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block p-3 hover:opacity-80 transition"
                style={{ backgroundColor: 'var(--color-widget-bg)' }}
              >
                <div className="flex gap-3">
                  {/* Thumbnail */}
                  {post.thumbnail && (
                    <div className="flex-shrink-0">
                      <img
                        src={post.thumbnail}
                        alt=""
                        className="w-16 h-16 object-cover rounded"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none'
                        }}
                      />
                    </div>
                  )}

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    {/* Title */}
                    <h3
                      className={`font-medium mb-1 ${isExpanded ? 'text-sm' : 'text-xs line-clamp-2'}`}
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      {post.title}
                    </h3>

                    {/* Text Preview - Expanded Only */}
                    {isExpanded && post.selftext && (
                      <p className="text-xs mb-2 line-clamp-2" style={{ color: 'var(--color-text-secondary)' }}>
                        {post.selftext}
                      </p>
                    )}

                    {/* Meta Info */}
                    <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                      {/* Score */}
                      <div className="flex items-center gap-1">
                        <ArrowUp size={12} style={{ color: 'var(--color-primary)' }} />
                        <span className="font-semibold">{formatScore(post.score)}</span>
                      </div>

                      {/* Comments */}
                      <div className="flex items-center gap-1">
                        <MessageCircle size={12} />
                        <span>{post.num_comments}</span>
                      </div>

                      {/* Author */}
                      <div className="flex items-center gap-1">
                        <span>u/{post.author}</span>
                      </div>

                      {/* Time */}
                      <div className="flex items-center gap-1">
                        <Clock size={12} />
                        <span>
                          {formatDistanceToNow(new Date(post.created_utc * 1000), { addSuffix: true })}
                        </span>
                      </div>

                      {/* External Link Icon */}
                      <div className="ml-auto">
                        <ExternalLink size={12} />
                      </div>
                    </div>

                    {/* Subreddit Badge - Only if viewing 'all' or 'frontpage' */}
                    {(selectedSubreddit === 'all' || selectedSubreddit === 'frontpage') && (
                      <div className="mt-2">
                        <span
                          className="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: 'var(--color-primary-100)',
                            color: 'var(--color-primary-700)',
                          }}
                        >
                          r/{post.subreddit}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}












